// 블로그 글 상세. /blog/:section/:slug
//
// 본문은 빌드 타임 스냅샷(blogPosts.generated.json)에서 동기로 읽는다. → 프리렌더 시
// HTML 에 본문이 그대로 박혀 크롤러가 읽을 수 있고, sitemap 에도 잡힌다.
//
// content 는 백엔드가 보관하는 신뢰된 HTML 이다(어드민 Tiptap 또는 마이그레이션 산출물).
// 사용자 입력이 아니므로 dangerouslySetInnerHTML 로 렌더한다.

import { Link, Navigate, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { getBlogPostBySlug, getBlogSection, getBlogPostsBySection } from '../data/blogPosts';
import { getAuthorAvatar } from '../data/authorAvatars';
import styles from './BlogPostPage.module.css';

const SITE_URL = 'https://medilawyer.co.kr';

function relatedPosts(post, limit = 3) {
  return getBlogPostsBySection(post.section)
    .filter((p) => p.slug !== post.slug)
    .slice(0, limit);
}

function articleJsonLd(post, path) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.publishedAt.replaceAll('.', '-'),
    author: { '@type': 'Person', name: post.author.name },
    publisher: {
      '@type': 'Organization',
      name: '메디로이어',
      url: SITE_URL,
    },
    mainEntityOfPage: `${SITE_URL}${path}`,
    ...(post.thumbnailUrl ? { image: post.thumbnailUrl } : {}),
  };
}

export function BlogPostPage() {
  const { section, slug } = useParams();
  const post = getBlogPostBySlug(slug);

  // 없는 글이거나 섹션이 안 맞으면 섹션 목록으로 돌려보낸다.
  if (!post) return <Navigate to="/blog" replace />;
  if (post.section !== section) {
    return <Navigate to={`/blog/${post.section}/${post.slug}`} replace />;
  }

  const sectionMeta = getBlogSection(post.section);
  const path = `/blog/${post.section}/${post.slug}`;
  const related = relatedPosts(post);
  const authorPhoto = getAuthorAvatar(post.author);

  return (
    <>
      <Seo
        title={`${post.metaTitle} — 메디로이어`}
        description={post.metaDescription}
        path={path}
        image={post.thumbnailUrl ?? undefined}
        jsonLd={articleJsonLd(post, path)}
      />

      <main className={styles.main}>
        <article className={styles.article}>
          <nav className={styles.crumbs} aria-label="위치">
            <Link to="/blog">미디어 센터</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/blog/${post.section}`}>{sectionMeta?.label}</Link>
          </nav>

          <header className={styles.header}>
            <h1 className={styles.title}>{post.title}</h1>
            {post.summary && <p className={styles.summary}>{post.summary}</p>}

            <div className={styles.meta}>
              {authorPhoto ? (
                <img
                  className={styles.avatarPhoto}
                  src={authorPhoto}
                  alt=""
                  width="32"
                  height="32"
                  decoding="async"
                />
              ) : (
                <span className={styles.avatar} aria-hidden="true">
                  {post.author.initials}
                </span>
              )}
              <span className={styles.authorName}>{post.author.name}</span>
              <span className={styles.dot} aria-hidden="true">
                ·
              </span>
              <time dateTime={post.publishedAt.replaceAll('.', '-')}>{post.publishedAt}</time>
              <span className={styles.dot} aria-hidden="true">
                ·
              </span>
              <span>{post.readMinutes}분 분량</span>
            </div>
          </header>

          {post.thumbnailUrl && (
            <img className={styles.hero} src={post.thumbnailUrl} alt="" width="1200" height="630" />
          )}

          {/* content 는 어드민(Tiptap) 또는 마이그레이션이 넣은 신뢰된 HTML 이다.
              사용자 입력이 아니므로 sanitize 없이 렌더한다. 외부 입력이 이 필드로
              들어오게 되면 반드시 sanitize 를 먼저 붙일 것. */}
          <div className={styles.body} dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.tags.length > 0 && (
            <ul className={styles.tags} aria-label="태그">
              {post.tags.map((tag) => (
                <li key={tag} className={styles.tag}>
                  {tag}
                </li>
              ))}
            </ul>
          )}
        </article>

        {related.length > 0 && (
          <aside className={styles.related} aria-labelledby="related-heading">
            <h2 id="related-heading" className={styles.relatedTitle}>
              {sectionMeta?.label} 다른 사례
            </h2>
            <ul className={styles.relatedList}>
              {related.map((item) => (
                <li key={item.slug}>
                  <Link to={`/blog/${item.section}/${item.slug}`} className={styles.relatedItem}>
                    <span className={styles.relatedItemTitle}>{item.title}</span>
                    <span className={styles.relatedItemDate}>{item.publishedAt}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </main>
    </>
  );
}
